import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Brain, Upload, Save, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'title_ar', ar: 'العنوان (عربي)', en: 'Title (Arabic)', area: true },
  { key: 'title_en', ar: 'العنوان (إنجليزي)', en: 'Title (English)', area: true },
  { key: 'subtitle_ar', ar: 'العنوان الفرعي (عربي)', en: 'Subtitle (Arabic)' },
  { key: 'subtitle_en', ar: 'العنوان الفرعي (إنجليزي)', en: 'Subtitle (English)' },
  { key: 'cta_ar', ar: 'نص الزر (عربي)', en: 'Button text (Arabic)' },
  { key: 'cta_en', ar: 'نص الزر (إنجليزي)', en: 'Button text (English)' },
];

// Admin CMS for the /psychology hero banner: bilingual text + banner image.
export const AdminPsychologyContentView = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/psychology/content`)
      .then((r) => setForm(r.data))
      .catch(() => toast.error(t('فشل التحميل', 'Failed to load')));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleImage = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('الصورة كبيرة جداً (2MB)', 'Image too large (max 2 MB)'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, banner_image: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/psychology-content`, form, { withCredentials: true });
      setForm(r.data);
      toast.success(t('تم الحفظ', 'Saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return <Card className="p-8 text-center text-sm text-gray-500" data-testid="admin-psy-content-loading">…</Card>;
  }

  return (
    <div className="space-y-4" data-testid="admin-psychology-content-view">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-bold text-[#0d47a1] flex items-center gap-2">
          <Brain size={18} /> {t('بانر صفحة علم النفس', 'Psychology Page Banner')}
        </h3>
        <Button onClick={save} disabled={saving} className="bg-[#1565c0] hover:bg-[#0d47a1]" data-testid="psy-content-save-btn">
          <Save size={14} className="me-1" />
          {saving ? t('جاري الحفظ...', 'Saving...') : t('حفظ', 'Save')}
        </Button>
      </div>

      <Card className="p-4 space-y-4">
        <div>
          <Label>{t('صورة البانر (خلفية)', 'Banner image (background)')}</Label>
          <div className="mt-2 flex items-center gap-3">
            <div className="w-40 h-24 rounded-xl bg-gradient-to-br from-[#0d2c54] to-[#123c73] overflow-hidden flex items-center justify-center flex-shrink-0">
              {form.banner_image
                ? <img src={form.banner_image} alt="" className="w-full h-full object-cover" data-testid="psy-banner-preview" />
                : <ImageIcon size={28} className="text-white/50" />}
            </div>
            <div className="space-y-2">
              <label className="cursor-pointer inline-block">
                <Button type="button" variant="outline" asChild>
                  <span><Upload size={14} className="me-1" /> {t('رفع صورة', 'Upload image')}</span>
                </Button>
                <input type="file" accept="image/*" className="hidden"
                       onChange={(e) => handleImage(e.target.files?.[0])}
                       data-testid="psy-banner-image-input" />
              </label>
              {form.banner_image && (
                <Button type="button" variant="ghost" className="text-red-500 block"
                        onClick={() => setForm({ ...form, banner_image: '' })}
                        data-testid="psy-banner-remove-btn">
                  {t('إزالة الصورة', 'Remove image')}
                </Button>
              )}
              <p className="text-xs text-gray-400">{t('بحد أقصى 2 ميجابايت', 'Max 2 MB')}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.area ? 'md:col-span-1' : ''}>
              <Label>{t(f.ar, f.en)}</Label>
              {f.area ? (
                <Textarea
                  rows={2}
                  value={form[f.key] || ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1"
                  data-testid={`psy-content-${f.key}`}
                />
              ) : (
                <Input
                  value={form[f.key] || ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1"
                  data-testid={`psy-content-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AdminPsychologyContentView;
